import { create } from 'zustand';
import {
    addEdge,
    applyEdgeChanges,
    applyNodeChanges,
    type Connection,
    type Edge,
    type EdgeChange,
    type Node,
    type NodeChange,
} from '@xyflow/react';
import { apiClient } from '../api/client';
import type {
    Project, Graph, Asset, Entity, VariableSet, Template,
} from '../api/types';
import { defaultDataFor } from '../model/nodeTypes/registry';
import { pinForSourceHandle, pinForTargetHandle, sourceHandleForPin, targetHandleForPin } from '../model/nodeTypes/pins';

// Must be a real UUID, not a placeholder like "node_1" — the backend's
// SaveGraphContentRequest validates nodes.*.uuid as `required|uuid`, and
// this id is used as-is (React Flow node id = node uuid throughout this
// app; see openGraph/saveGraph below), so a non-UUID id fails on save.
//
// crypto.randomUUID() only exists in secure contexts (HTTPS or literal
// `localhost`) — a dev site served over plain http://*.test (e.g. Herd's
// default) doesn't get it at all, throwing "crypto.randomUUID is not a
// function" the moment any node is created (including palette
// drag-and-drop, which is where this was first noticed). Fall back to
// building an RFC 4122 v4 UUID from crypto.getRandomValues(), which has no
// such secure-context restriction.
export const genId = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * Group frame membership (doc 06 §1: Group frame uses React Flow's
 * `parentId` subflow so dragging the frame moves its members). The backend
 * has no dedicated parent column — `graph_nodes.data` is a plain JSON blob
 * — so membership is carried as `data.parent_uuid` on the wire (doc 03 §2:
 * "children store parent_uuid in data — mapped to React Flow's parentId at
 * hydrate time"), and translated to/from React Flow's `parentId` at the
 * load/save boundary here. The backend always stores plain absolute x/y;
 * React Flow requires a child's `position` to be relative to its parent
 * when `parentId` is set, so that translation happens in both directions:
 * inline in `openGraph` below (hydrating), and via `resolveAbsolutePosition`
 * in `saveGraph` (persisting).
 */

/** React Flow requires parent nodes to appear before their children in the nodes array. */
export function orderNodesParentFirst<T extends { id: string; parentId?: string }>(nodes: T[]): T[] {
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const depthCache = new Map<string, number>();

    const depthOf = (id: string, guard: Set<string> = new Set()): number => {
        if (depthCache.has(id)) return depthCache.get(id)!;
        const node = byId.get(id);
        if (!node?.parentId || guard.has(id)) return 0; // no parent, or a cyclic parentId chain (malformed data) — treat as root
        const d = 1 + depthOf(node.parentId, new Set(guard).add(id));
        depthCache.set(id, d);
        return d;
    };

    // Array.prototype.sort is a stable sort (guaranteed since ES2019), so
    // nodes at the same depth keep their original relative order.
    return [...nodes].sort((a, b) => depthOf(a.id) - depthOf(b.id));
}

/**
 * Resolve a node's absolute canvas position by walking up its parentId
 * chain, since a grouped node's `position` is relative to its immediate
 * parent (which may itself be grouped). Used when saving, to always send
 * the backend plain absolute x/y regardless of nesting.
 */
export function resolveAbsolutePosition(
    nodeId: string,
    byId: Map<string, Node>,
    memo: Map<string, { x: number; y: number }> = new Map(),
    guard: Set<string> = new Set()
): { x: number; y: number } {
    if (memo.has(nodeId)) return memo.get(nodeId)!;

    const node = byId.get(nodeId);
    if (!node) return { x: 0, y: 0 };

    if (!node.parentId || guard.has(nodeId)) {
        // No parent, or a cyclic parentId chain (malformed data) — treat
        // this node's own position as absolute rather than recursing forever.
        memo.set(nodeId, node.position);
        return node.position;
    }

    const parentAbs = resolveAbsolutePosition(node.parentId, byId, memo, new Set(guard).add(nodeId));
    const abs = { x: parentAbs.x + node.position.x, y: parentAbs.y + node.position.y };
    memo.set(nodeId, abs);
    return abs;
}

interface ProjectState {
    // Data
    projects: Project[];
    currentProject: Project | null;
    currentGraph: Graph | null;
    /** Root-to-current ancestry path for the open graph (doc 04's nested-graph breadcrumbs). */
    breadcrumb: Array<{ uuid: string; name: string }>;
    nodes: Node[];
    edges: Edge[];

    // Catalogs (per-project, loaded once)
    assets: Asset[];
    entities: Entity[];
    variableSets: VariableSet[];
    templates: Template[];

    // UI state
    isLoading: boolean;
    error: string | null;
    isDirty: boolean;
    catalogsDirty: boolean;

    // Actions
    loadProjects: () => Promise<void>;
    openProject: (projectUuid: string) => Promise<void>;
    createProject: (data: { name: string; slug: string; description?: string }) => Promise<void>;
    openGraph: (graphUuid: string) => Promise<void>;
    loadCatalogs: (projectUuid: string) => Promise<void>;

    // Canvas mutations
    onNodesChange: (changes: NodeChange[]) => void;
    onEdgesChange: (changes: EdgeChange[]) => void;
    onConnect: (connection: Connection) => void;
    addNodeAt: (type: string, position: { x: number; y: number }, dataOverride?: Record<string, unknown>) => void;

    // Catalog mutations
    addAsset: (asset: Asset) => void;
    removeAsset: (assetUuid: string) => void;
    addEntity: (entity: Entity) => void;
    updateEntity: (uuid: string, data: Partial<Entity>) => void;
    removeEntity: (uuid: string) => void;
    addVariableSet: (set: VariableSet) => void;
    removeVariableSet: (uuid: string) => void;
    addTemplate: (template: Template) => void;
    updateTemplate: (uuid: string, data: Partial<Template>) => void;
    removeTemplate: (uuid: string) => void;

    // Persistence
    saveGraph: () => Promise<void>;
    saveCatalogs: () => Promise<void>;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
    // Initial state
    projects: [],
    currentProject: null,
    currentGraph: null,
    breadcrumb: [],
    nodes: [],
    edges: [],
    assets: [],
    entities: [],
    variableSets: [],
    templates: [],
    isLoading: false,
    error: null,
    isDirty: false,
    catalogsDirty: false,

    // Load projects for the current user
    loadProjects: async () => {
        set({ isLoading: true, error: null });
        try {
            const projects = await apiClient.listProjects();
            set({ projects });
        } catch (err) {
            set({ error: (err as Error).message });
        } finally {
            set({ isLoading: false });
        }
    },

    // Create a new project
    createProject: async (data) => {
        set({ isLoading: true, error: null });
        try {
            const { project: newProject } = await apiClient.createProject(data);
            set((state) => ({ projects: [...state.projects, newProject] }));
        } catch (err) {
            set({ error: (err as Error).message });
        } finally {
            set({ isLoading: false });
        }
    },

    // Open a project (load its root graph and catalogs)
    openProject: async (projectUuid: string) => {
        set({ isLoading: true, error: null });
        try {
            const { project, rootGraph } = await apiClient.getProject(projectUuid);
            set({ currentProject: project });

            // Load catalogs in parallel with root graph
            await Promise.all([
                get().openGraph(rootGraph.uuid),
                get().loadCatalogs(projectUuid),
            ]);
        } catch (err) {
            set({ error: (err as Error).message });
        } finally {
            set({ isLoading: false });
        }
    },

    // Open a graph (load its nodes and connections)
    openGraph: async (graphUuid: string) => {
        set({ isLoading: true, error: null, isDirty: false });
        try {
            const { graph, nodes, connections, breadcrumb } = await apiClient.getGraph(graphUuid);

            // The backend always stores plain absolute x/y (see the block
            // comment above genId()). Build an absolute-position lookup
            // first, from the raw untransformed data, so relative-to-parent
            // positions can be computed for grouped nodes regardless of
            // nesting depth or array order.
            const absoluteById = new Map(nodes.map((n) => [n.uuid, { x: n.x, y: n.y }]));

            const hydratedNodes = nodes.map((n) => {
                const parentUuid = (n.data as Record<string, unknown> | null)?.parent_uuid as string | undefined;
                const parentAbs = parentUuid ? absoluteById.get(parentUuid) : undefined;
                const selfAbs = absoluteById.get(n.uuid)!;

                return {
                    id: n.uuid,
                    type: n.type,
                    position: parentAbs
                        ? { x: selfAbs.x - parentAbs.x, y: selfAbs.y - parentAbs.y }
                        : selfAbs,
                    parentId: parentAbs ? parentUuid : undefined,
                    data: n.data || {},
                    width: n.width,
                    height: n.height,
                    style: n.color ? { backgroundColor: n.color } : undefined,
                };
            });

            // Resolve each connection's source_pin/target_pin (numeric,
            // node-type-agnostic on the wire) back into the actual React Flow
            // handle id its endpoint node uses (e.g. Condition's 'true'/
            // 'false') — see model/nodeTypes/pins.ts for why this can't be a
            // fixed lookup. Without this, every edge reconnects to whichever
            // handle pin 0 happens to be, regardless of which branch it was.
            const nodesByUuid = new Map(nodes.map((n) => [n.uuid, n]));

            set({
                currentGraph: graph,
                breadcrumb: breadcrumb || [],
                // React Flow requires parents to precede their children in the array.
                nodes: orderNodesParentFirst(hydratedNodes),
                edges: connections.map((c) => {
                    const sourceNode = nodesByUuid.get(c.source_node_uuid);
                    const targetNode = nodesByUuid.get(c.target_node_uuid);
                    return {
                        id: c.uuid,
                        source: c.source_node_uuid,
                        target: c.target_node_uuid,
                        sourceHandle: sourceHandleForPin(sourceNode?.type, (sourceNode?.data as Record<string, unknown>) ?? {}, c.source_pin),
                        targetHandle: targetHandleForPin(targetNode?.type, (targetNode?.data as Record<string, unknown>) ?? {}, c.target_pin),
                        label: c.label,
                        style: c.color ? { stroke: c.color } : undefined,
                        data: { condition: c.condition },
                    };
                }),
            });
        } catch (err) {
            set({ error: (err as Error).message });
        } finally {
            set({ isLoading: false });
        }
    },

    // React Flow node/edge changes
    onNodesChange: (changes) => {
        set((state) => ({
            nodes: applyNodeChanges(changes, state.nodes),
            isDirty: true,
        }));
    },

    onEdgesChange: (changes) => {
        set((state) => ({
            edges: applyEdgeChanges(changes, state.edges),
            isDirty: true,
        }));
    },

    onConnect: (connection) => {
        set((state) => ({
            // React Flow's default edge id ("xy-edge__source-target") isn't
            // a valid UUID — the backend's SaveGraphContentRequest requires
            // connections.*.uuid as `required|uuid`, so override it here
            // (same reasoning as genId() above for nodes).
            edges: addEdge(connection, state.edges, { getEdgeId: () => crypto.randomUUID() }),
            isDirty: true,
        }));
    },

    // Add a new node at a position, optionally seeding its data (e.g. a
    // tilemap_ref dropped from the tilemap catalog arrives already pointing at
    // its map).
    addNodeAt: (type, position, dataOverride) => {
        const data = defaultDataFor(type);
        if (!data) return;

        const node: Node = {
            id: genId(),
            type,
            position,
            data: dataOverride ? { ...data, ...dataOverride } : data,
        };

        set((state) => ({
            nodes: [...state.nodes, node],
            isDirty: true,
        }));
    },

    // Load all project catalogs
    loadCatalogs: async (projectUuid: string) => {
        try {
            const [assets, entities, variableSets, templates] = await Promise.all([
                apiClient.listAssets(projectUuid),
                apiClient.listEntities(projectUuid),
                apiClient.listVariableSets(projectUuid),
                apiClient.listTemplates(projectUuid),
            ]);

            set({
                assets: Array.isArray(assets) ? assets : [],
                entities: Array.isArray(entities) ? entities : [],
                variableSets: Array.isArray(variableSets) ? variableSets : [],
                templates: Array.isArray(templates) ? templates : [],
            });
        } catch (err) {
            set({ error: (err as Error).message });
        }
    },

    // Catalog mutations
    addAsset: (asset) => {
        set((state) => ({
            assets: [...state.assets, asset],
            catalogsDirty: true,
        }));
    },

    removeAsset: (assetUuid) => {
        set((state) => ({
            assets: state.assets.filter((a) => a.uuid !== assetUuid),
            catalogsDirty: true,
        }));
    },

    addEntity: (entity) => {
        set((state) => ({
            entities: [...state.entities, entity],
            catalogsDirty: true,
        }));
    },

    updateEntity: (uuid, data) => {
        set((state) => ({
            entities: state.entities.map((e) =>
                e.uuid === uuid ? { ...e, ...data } : e
            ),
            catalogsDirty: true,
        }));
    },

    removeEntity: (uuid) => {
        set((state) => ({
            entities: state.entities.filter((e) => e.uuid !== uuid),
            catalogsDirty: true,
        }));
    },

    addVariableSet: (set_) => {
        set((state) => ({
            variableSets: [...state.variableSets, set_],
            catalogsDirty: true,
        }));
    },

    removeVariableSet: (uuid) => {
        set((state) => ({
            variableSets: state.variableSets.filter((v) => v.uuid !== uuid),
            catalogsDirty: true,
        }));
    },

    addTemplate: (template) => {
        set((state) => ({
            templates: [...state.templates, template],
            catalogsDirty: true,
        }));
    },

    updateTemplate: (uuid, data) => {
        set((state) => ({
            templates: state.templates.map((t) =>
                t.uuid === uuid ? { ...t, ...data } : t
            ),
            catalogsDirty: true,
        }));
    },

    removeTemplate: (uuid) => {
        set((state) => ({
            templates: state.templates.filter((t) => t.uuid !== uuid),
            catalogsDirty: true,
        }));
    },

    // Save the current graph to the API
    saveGraph: async () => {
        const { currentGraph, nodes, edges } = get();
        if (!currentGraph) return;

        set({ isLoading: true, error: null });
        try {
            // Map React Flow nodes back to API format. The backend always
            // stores plain absolute x/y (see the block comment above
            // genId()), so a grouped node's position — relative to its
            // parent in React Flow's state — is resolved back to absolute
            // here, and its parentId (if any) is written into
            // data.parent_uuid, the wire format for group membership.
            const nodesById = new Map(nodes.map((n) => [n.id, n]));
            const absoluteMemo = new Map<string, { x: number; y: number }>();

            const apiNodes = nodes.map((n) => {
                const abs = resolveAbsolutePosition(n.id, nodesById, absoluteMemo);

                // Rebuild `data` without a stale parent_uuid, then only
                // re-add it if the node is currently grouped — otherwise a
                // node dragged out of a group would keep reporting its old
                // parent_uuid forever, since it's not derived from anything
                // else in `data`.
                const { parent_uuid: _drop, ...dataWithoutParent } = (n.data ?? {}) as Record<string, unknown>;
                const data = n.parentId ? { ...dataWithoutParent, parent_uuid: n.parentId } : dataWithoutParent;

                return {
                    uuid: n.id,
                    type: n.type || 'note',
                    x: abs.x,
                    y: abs.y,
                    width: n.width,
                    height: n.height,
                    data,
                };
            });

            // Map React Flow edges back to API format.
            // Edge source/target are node uuids (React Flow node ids are
            // set to the node uuid in openGraph), matching the wire format
            // the backend resolves against `graph_nodes.uuid`.
            // sourceHandle/targetHandle (e.g. Condition's 'true'/'false') are
            // encoded as the numeric source_pin/target_pin the backend
            // stores, via the same per-node-type registry openGraph() uses
            // to decode them back (model/nodeTypes/pins.ts) — otherwise a
            // Condition node's branch identity is lost the moment autosave
            // fires (see GraphContentPersistenceTest.php).
            const apiConnections = edges.map((e) => {
                const sourceNode = nodesById.get(e.source);
                const targetNode = nodesById.get(e.target);
                return {
                    uuid: e.id,
                    source_node_uuid: e.source,
                    source_pin: pinForSourceHandle(sourceNode?.type, (sourceNode?.data as Record<string, unknown>) ?? {}, e.sourceHandle),
                    target_node_uuid: e.target,
                    target_pin: pinForTargetHandle(targetNode?.type, (targetNode?.data as Record<string, unknown>) ?? {}, e.targetHandle),
                    label: e.label ? String(e.label) : undefined,
                    condition: e.data?.condition,
                };
            });

            const result = await apiClient.saveGraphContent(currentGraph.uuid, {
                nodes: apiNodes as any,
                connections: apiConnections as any,
            });

            // An Entry/Exit the client removed locally may have been kept
            // server-side (doc 04's pin pass-through protection — see
            // GraphController::saveContent). That's a real, surprising
            // divergence between client and server state (the node will
            // reappear on next reload) worth surfacing rather than
            // silently marking the save clean.
            if (result.skipped_deletions?.length) {
                set({
                    error: `${result.skipped_deletions.length} entry/exit node(s) could not be removed because they still have connections — they were kept on the server and will reappear on reload.`,
                });
            }

            set({ isDirty: false });
        } catch (err) {
            set({ error: (err as Error).message });
        } finally {
            set({ isLoading: false });
        }
    },

    // Save catalogs (placeholder for now; would batch catalog changes in real autosave)
    saveCatalogs: async () => {
        const { currentProject } = get();
        if (!currentProject) return;

        try {
            // In a full implementation, would flush catalog changes here
            set({ catalogsDirty: false });
        } catch (err) {
            set({ error: (err as Error).message });
        }
    },

    /**
     * Create a child graph for a container node
     *
     * Initiates lazy creation of a child graph when a container is opened.
     * The API creates Entry/Exit nodes automatically to enable pin pass-through.
     *
     * **Process:**
     * 1. POST /nodes/{nodeUuid}/child-graph
     * 2. GraphService creates child graph + Entry/Exit nodes
     * 3. Update container node's data with childGraphUuid
     * 4. Automatically navigate to the new child graph
     *
     * **Entry/Exit Nodes:**
     * The child graph arrives pre-populated with:
     * - Entry nodes on the left (for inputs from parent)
     * - Exit nodes on the right (for outputs to parent)
     * - Pins matched to container's input/output count
     *
     * **Example:**
     * Container has 1 input + 3 outputs → child has 1 Entry + 3 Exit nodes
     *
     * @async
     * @param {string} nodeUuid - UUID of container node
     * @returns {Promise<string>} UUID of newly created child graph
     *
     * @throws {Error} If node is not a container or max depth exceeded
     *
     * @example
     * // User double-clicks a container
     * const childGraphUuid = await createChildGraph(containerId);
     * await openGraph(childGraphUuid);
     */
    createChildGraph: async (nodeUuid: string) => {
        set({ isLoading: true, error: null });
        try {
            const childGraph = await apiClient.createChildGraph(nodeUuid);
            return childGraph.uuid;
        } catch (err) {
            set({ error: (err as Error).message });
            throw err;
        } finally {
            set({ isLoading: false });
        }
    },

    /**
     * Save viewport state for current graph
     *
     * Persists the user's camera position (pan/zoom) so that exiting and
     * re-entering the graph restores the exact view. Called automatically
     * by EditorShell when viewport changes.
     *
     * **Viewport Format:**
     * ```typescript
     * {
     *   x: -150.5,      // pan x (can be negative)
     *   y: 280.3,       // pan y (can be negative)
     *   zoom: 1.2       // magnification (1.0 = 100%)
     * }
     * ```
     *
     * This state is returned with every GET /graphs/{uuid} request, enabling
     * seamless context restoration across navigation and page reloads.
     *
     * @async
     * @param {Object} viewport - Camera position
     * @param {number} viewport.x - Pan position X
     * @param {number} viewport.y - Pan position Y
     * @param {number} viewport.zoom - Zoom level
     *
     * @example
     * // Save when user pans/zooms (called by EditorShell)
     * await saveViewport({ x: -100, y: 50, zoom: 1.5 });
     */
    saveViewport: async (viewport: { x: number; y: number; zoom: number }) => {
        const { currentGraph } = get();
        if (!currentGraph) return;

        try {
            await apiClient.saveViewport(currentGraph.uuid, viewport);
        } catch (err) {
            console.error('Failed to save viewport:', err);
            // Don't set error state for viewport saves - non-critical
        }
    },
}));
